import type { DetectionRegion, ExportOptions, Rect } from '../types'
import { clampRect } from './rect'

function drawMosaic(ctx: CanvasRenderingContext2D, source: CanvasImageSource, rect: Rect, blockSize: number): void {
  const width = Math.max(1, Math.round(rect.width / blockSize))
  const height = Math.max(1, Math.round(rect.height / blockSize))
  const scratch = document.createElement('canvas')
  scratch.width = width
  scratch.height = height
  const scratchCtx = scratch.getContext('2d')
  if (!scratchCtx) return
  scratchCtx.imageSmoothingEnabled = true
  scratchCtx.drawImage(source, rect.x, rect.y, rect.width, rect.height, 0, 0, width, height)
  ctx.save()
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(scratch, 0, 0, width, height, rect.x, rect.y, rect.width, rect.height)
  ctx.restore()
}

function drawBlur(ctx: CanvasRenderingContext2D, source: CanvasImageSource, rect: Rect, radius: number): void {
  // CanvasRenderingContext2D.filter is not consistently available in every
  // supported browser. A destructive downsample/upscale pipeline gives a
  // portable visual blur without leaving the original pixels in the output.
  const strength = Math.max(6, Math.round(radius))
  const smallWidth = Math.max(1, Math.round(rect.width / strength))
  const smallHeight = Math.max(1, Math.round(rect.height / strength))
  const scratch = document.createElement('canvas')
  scratch.width = smallWidth
  scratch.height = smallHeight
  const scratchCtx = scratch.getContext('2d')
  if (!scratchCtx) return
  scratchCtx.imageSmoothingEnabled = true
  scratchCtx.imageSmoothingQuality = 'high'
  scratchCtx.drawImage(source, rect.x, rect.y, rect.width, rect.height, 0, 0, smallWidth, smallHeight)

  const softened = document.createElement('canvas')
  softened.width = Math.max(1, Math.round(rect.width))
  softened.height = Math.max(1, Math.round(rect.height))
  const softenedCtx = softened.getContext('2d')
  if (!softenedCtx) return
  softenedCtx.imageSmoothingEnabled = true
  softenedCtx.imageSmoothingQuality = 'high'
  softenedCtx.drawImage(scratch, 0, 0, smallWidth, smallHeight, 0, 0, softened.width, softened.height)

  ctx.save()
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(softened, rect.x, rect.y, rect.width, rect.height)
  ctx.restore()
}

export function renderRedactedCanvas(
  source: CanvasImageSource,
  width: number,
  height: number,
  regions: DetectionRegion[],
  options: ExportOptions,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d', { alpha: options.mime !== 'image/jpeg' })
  if (!ctx) throw new Error('Canvas 2Dを利用できません')
  if (options.mime === 'image/jpeg') {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
  }
  ctx.drawImage(source, 0, 0, width, height)
  const active = regions.filter((item) => item.enabled).map((region) => ({
    rect: clampRect(region.rect, width, height),
    mode: region.mode ?? options.mode,
  })).filter((item) => item.rect.width >= 1 && item.rect.height >= 1)

  // Apply visual effects from the immutable source first, then apply solid
  // redactions last. This avoids allocating another full-resolution snapshot
  // and prevents an overlapping blur/mosaic from revealing a blacked-out area.
  for (const { rect, mode } of active.filter((item) => item.mode === 'mosaic' || item.mode === 'blur')) {
    if (mode === 'mosaic') {
      const block = Math.max(8, Math.round(Math.min(rect.width, rect.height) / 10))
      drawMosaic(ctx, source, rect, block)
    } else {
      drawBlur(ctx, source, rect, Math.max(12, Math.round(Math.min(width, height) / 80)))
    }
  }
  for (const { rect, mode } of active.filter((item) => item.mode === 'solid' || item.mode === 'fill')) {
    ctx.fillStyle = mode === 'solid' ? '#000000' : options.fillColor
    ctx.fillRect(Math.floor(rect.x), Math.floor(rect.y), Math.ceil(rect.width), Math.ceil(rect.height))
  }
  return canvas
}

export function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('画像の再生成に失敗しました')), mime, quality)
  })
}
